import type { Lang, LorsCopy } from "./translations";

// July 16, 2026 bilingual workshop content. Mirrors the shared `translations.ts`
// (May 13) format so <LorsHero>, <LorsTestimonials>, <LorsAbout>, etc. can
// consume it without any component changes. Only date-specific strings differ.
const content = {

  en: {
    eyebrow: "Free Estate Planning Workshop",
    headline:
      "Learn How Santa Rosa Families Avoid Paying Up to $50,000+ in Probate Cost",
    headlineBreakBefore: "Probate Cost",
    subhead:
      "Join us for a free in-person event to help your family avoid court, conflict & confusion.",
    subheadLabel: "A note from Ruby:",
    learnTitle: "What Most Families Don't Realize Until It's Too Late",
    learnItems: [
      "Your will won't keep your family out of probate, and what you need instead",
      "There is one document all homeowners must have to transfer their home quickly, so their family isn't stuck in a year-long legal battle",
      "Your spouse may not be able to sell your house or access your joint bank accounts without the right documents in place",
      "Why the legal mess your family inherits is almost always preventable, if you understand the California laws",
    ],
    quote:
      "Ruby Steinbrecher was fantastic. The family trust workshop was clear, informative, and extremely helpful for getting started with estate planning.",
    quoteAttribution: "Shawn H., Workshop Attendee",
    formDate: { short: "Thu, July 16th", long: "Thursday, July 16th" },
    formTime: "4:00–5:00 PM",
    formVenueLine1: "North Coast Builders Exchange",
    formVenueLine2: "1030 Apollo Way (Training Center Room), Santa Rosa, CA 95407",
    formTitle: "Reserve Your Seat",
    formSubtitle: "Free to attend. Limited seating available.",
    formFirstName: "First name",
    formLastName: "Last name",
    formEmail: "Email",
    formPhone: "Phone",
    formSmsConsent:
      "I consent to receive text messages from the Law Office of Ruby Steinbrecher about this event, and occasional marketing messages. Message & data rates may apply. Text HELP for assistance, reply STOP to opt out.",
    formEnewsConsent:
      "I'd like to receive occasional email updates from the Law Office of Ruby Steinbrecher.",
    formPrivacy: "Privacy Policy",
    formSubmit: "Reserve My Seat",
    formSubmitting: "Reserving your seat…",
    formTrust: "⭐ Trusted by local families | 81 Five-Star Reviews",
    getDirections: "Get Directions",
    footerCopyright: "© 2026 Ruby Steinbrecher",
    footerPrivacy: "Privacy Policy",
    testimonialsTitle: "What Families Are Saying",
    aboutTitle: "Meet Ruby Steinbrecher",
    aboutRole: "Attorney",
    aboutBody: [
      "I have practiced law since 2007, first working at a medium-sized firm in San Francisco handling civil litigation, and then moving into estate planning, small business and contract law since 2010. I value people, the planet, and prosperity that does not adversely affect the former.",
      "Prior to entering the legal field, I had a variety of business and life experience that has only enhanced my ability to serve clients: Road Manager for Ken Kesey's bus trip to Great Britain in 1999, Human Resources at a tech startup in 2001, recognized for my work as a Case Manager at Casey Family Programs with disadvantaged youth in the foster care system in Oakland and the wider San Francisco-Bay Area in the early 2000's.",
      "Currently, I live in Sebastopol with my daughter, husband, and a menagerie of pets, where I enjoy gardening as it keeps me grounded and enjoying the beautiful weather.",
    ],
    registrationTitle: "Ready to Secure Your Family's Future?",
    registrationSub: "Free to attend. Limited seating available.",
    registrationCta: "Save My Seat",
    registrationFoot:
      "No pressure. Just clear information so you can make the right decision for your family.",
    errorTitle: "Something went wrong",
    errorBody:
      "We couldn't reserve your seat. Please try again or call our office.",
    validation: {
      firstName: "First name is required",
      lastName: "Last name is required",
      email: "Enter a valid email",
      phone: "Enter a valid phone number",
    },
    timeAgo: {
      monthAgo: "a month ago",
      monthsAgo: (n: number) => `${n} months ago`,
      yearAgo: "a year ago",
    },
  },
  es: {
    eyebrow: "Taller Gratuito de Planificación Patrimonial",
    headline:
      "Aprenda Cómo las Familias de Santa Rosa Evitan Pagar Más de $50,000 en Costos de Sucesión",
    headlineBreakBefore: "Costos de Sucesión",
    subhead:
      "Únase a nosotros para un evento presencial gratuito que ayudará a su familia a evitar la corte, el conflicto y la confusión.",
    subheadLabel: "Una nota de Ruby:",
    learnTitle: "Lo Que la Mayoría de las Familias No Saben Hasta Que Es Demasiado Tarde",
    learnItems: [
      "Su testamento no mantendrá a su familia fuera de la sucesión, y lo que necesita en su lugar",
      "Existe un documento que todos los propietarios deben tener para transferir su casa rápidamente, para que su familia no quede atrapada en una batalla legal de un año",
      "Es posible que su cónyuge no pueda vender su casa ni acceder a sus cuentas bancarias conjuntas sin los documentos adecuados",
      "Por qué el lío legal que hereda su familia casi siempre se puede evitar, si comprende las leyes de California",
    ],
    quote:
      "Ruby Steinbrecher fue fantástica. El taller del fideicomiso familiar fue claro, informativo y extremadamente útil para empezar con la planificación patrimonial.",
    quoteAttribution: "Shawn H., Asistente al Taller",
    formDate: { short: "Jue, 16 de Julio", long: "Jueves, 16 de Julio" },
    formTime: "4:00–5:00 PM",
    formVenueLine1: "North Coast Builders Exchange",
    formVenueLine2: "1030 Apollo Way (Training Center Room), Santa Rosa, CA 95407",
    formTitle: "Reserve Su Asiento",
    formSubtitle: "Asistencia gratuita. Cupos limitados.",
    formFirstName: "Nombre",
    formLastName: "Apellido",
    formEmail: "Correo electrónico",
    formPhone: "Teléfono",
    formSmsConsent:
      "Doy mi consentimiento para recibir mensajes de texto de la Oficina Legal de Ruby Steinbrecher sobre este evento y mensajes de marketing ocasionales. Pueden aplicarse tarifas por mensajes y datos. Envíe HELP para asistencia, responda STOP para cancelar.",
    formEnewsConsent:
      "Me gustaría recibir actualizaciones por correo electrónico ocasionales de la Oficina Legal de Ruby Steinbrecher.",
    formPrivacy: "Política de Privacidad",
    formSubmit: "Reservar Mi Asiento",
    formSubmitting: "Reservando su asiento…",
    formTrust: "⭐ Confiado por familias locales | 81 Reseñas de Cinco Estrellas",
    getDirections: "Cómo Llegar",
    footerCopyright: "© 2026 Ruby Steinbrecher",
    footerPrivacy: "Política de Privacidad",
    testimonialsTitle: "Lo Que Dicen las Familias",
    aboutTitle: "Conozca a Ruby Steinbrecher",
    aboutRole: "Abogada",
    aboutBody: [
      "He ejercido la abogacía desde 2007, primero trabajando en un bufete mediano en San Francisco manejando litigios civiles, y luego pasando a la planificación patrimonial, pequeñas empresas y derecho contractual desde 2010. Valoro a las personas, el planeta y la prosperidad que no afecta negativamente a los primeros.",
      "Antes de entrar en el campo legal, tuve una variedad de experiencias comerciales y de vida que solo han mejorado mi capacidad para servir a los clientes: Gerente de Gira del viaje en autobús de Ken Kesey a Gran Bretaña en 1999, Recursos Humanos en una startup tecnológica en 2001, reconocida por mi trabajo como Gerente de Casos en Casey Family Programs con jóvenes desfavorecidos en el sistema de cuidado de crianza en Oakland y el área de la Bahía de San Francisco a principios de los 2000.",
      "Actualmente, vivo en Sebastopol con mi hija, mi esposo y una variedad de mascotas, donde disfruto de la jardinería ya que me mantiene con los pies en la tierra y disfrutando del hermoso clima.",
    ],
    registrationTitle: "¿Listo para Asegurar el Futuro de Su Familia?",
    registrationSub: "Asistencia gratuita. Cupos limitados.",
    registrationCta: "Reservar Mi Asiento",
    registrationFoot:
      "Sin presión. Solo información clara para que pueda tomar la decisión correcta para su familia.",
    errorTitle: "Algo salió mal",
    errorBody:
      "No pudimos reservar su asiento. Por favor inténtelo de nuevo o llame a nuestra oficina.",
    validation: {
      firstName: "El nombre es obligatorio",
      lastName: "El apellido es obligatorio",
      email: "Ingrese un correo válido",
      phone: "Ingrese un teléfono válido",
    },
    timeAgo: {
      monthAgo: "hace un mes",
      monthsAgo: (n: number) => `hace ${n} meses`,
      yearAgo: "hace un año",
    },
  },
};

export const lorsContentJuly16 = content as unknown as Record<Lang, LorsCopy>;

